import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { 
  Users, 
  Award, 
  Search, 
  Settings, 
  TrendingUp, 
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export const ExpertisePage = () => {
  const expertiseAreas = [
    {
      icon: <Users className="h-12 w-12 text-blue-600" />,
      title: "Hands-on Practitioners",
      description: "Led GMP implementations in live plants, QC labs, and CMOs with Quality Assurance leadership in regulated environments",
      highlights: [
        "Direct experience implementing GMP systems in operational facilities",
        "Quality Assurance leadership roles in pharmaceutical manufacturing",
        "Hands-on management of manufacturing and laboratory compliance programs",
        "Real-world problem solving in high-pressure regulatory environments"
      ]
    },
    {
      icon: <Award className="h-12 w-12 text-green-600" />,
      title: "Subject-Matter Expert (Quality Systems)",
      description: "Deep expertise in quality systems, QMS design, validations, and comprehensive risk assessments",
      highlights: [
        "QMS design and implementation across pharmaceutical operations",
        "Validation protocols and execution for systems and processes", 
        "Risk assessments using ICH Q9 methodology and frameworks",
        "Gap assessments for regulatory compliance and system optimization",
        "Quality system integration and lifecycle management"
      ]
    },
    {
      icon: <Search className="h-12 w-12 text-purple-600" />,
      title: "Review & Guidance for Investigations",
      description: "Systematic approach to deviation root-cause analysis, CAPA effectiveness, and data-driven trending",
      highlights: [
        "Root cause analysis methodology and execution",
        "CAPA system design and effectiveness measurement",
        "Statistical trending and analysis for continuous improvement",
        "Investigation report writing and regulatory presentation",
        "Cross-functional team leadership during complex investigations"
      ]
    },
    {
      icon: <Settings className="h-12 w-12 text-orange-600" />,
      title: "Hybrid Strength: Quality, Operations, and IT",
      description: "Unique combination bridging validation, QA, and enterprise systems (MES/LIMS/ERP)",
      highlights: [
        "Computer system validation across manufacturing and laboratory systems",
        "Integration of quality systems with manufacturing execution systems",
        "LIMS implementation and validation for laboratory operations",
        "ERP quality module configuration and validation",
        "Cross-functional expertise spanning IT, Quality, and Operations"
      ]
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-indigo-600" />,
      title: "Right-Sizing Over Gold-Plating",
      description: "Lean templates, risk-based effort, and scalable governance that grows with your organization",
      highlights: [
        "Risk-based approach to compliance program design",
        "Scalable document templates and procedures",
        "Cost-effective solutions that don't compromise quality",
        "Lean governance structures that enable business growth",
        "Practical implementation strategies for resource-constrained environments"
      ]
    }
  ];

  const industries = [
    {
      name: "Small Molecule Pharmaceuticals",
      description: "Traditional pharmaceutical manufacturing with focus on chemical API production and solid dosage forms"
    },
    {
      name: "Biotech & Biologics", 
      description: "Biotechnology companies developing and manufacturing biological products, vaccines, and cell therapies"
    },
    {
      name: "Active Pharmaceutical Ingredients (APIs)",
      description: "Chemical manufacturing facilities producing active ingredients for pharmaceutical products"
    },
    {
      name: "Contract Development & Manufacturing Organizations (CDMOs)",
      description: "Service providers offering development and manufacturing services to pharmaceutical companies"
    },
    {
      name: "Nutraceuticals",
      description: "Nutritional supplement manufacturers operating under FDA dietary supplement regulations"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-navy-900 mb-6">
              Our <span className="text-blue-600">Expertise</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Deep, hands-on experience across the pharmaceutical and biotech landscape. 
              We bring practical knowledge from the manufacturing floor to the boardroom, 
              ensuring compliance strategies that work in the real world.
            </p>
          </div>
        </div>
      </section>

      {/* Core Expertise Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Core Expertise Areas</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our multidisciplinary background enables us to see the big picture while 
              delivering precise, actionable solutions.
            </p>
          </div>

          <div className="space-y-16">
            {expertiseAreas.map((area, index) => (
              <Card key={index} className="shadow-xl border-0">
                <CardHeader>
                  <div className="flex items-center space-x-6 mb-4">
                    {area.icon}
                    <div>
                      <CardTitle className="text-2xl text-navy-900 mb-2">{area.title}</CardTitle>
                      <p className="text-lg text-gray-600">{area.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {area.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expertise spans the full spectrum of regulated industries, 
              from traditional pharmaceuticals to cutting-edge biotechnology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-lg text-navy-900 mb-2">{industry.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{industry.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Metrics */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">
              Experience That <span className="text-blue-600">Counts</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">36+</div>
              <div className="text-lg font-semibold text-navy-900 mb-1">Years Combined</div>
              <div className="text-gray-600">GMP & GxP Leadership</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">100+</div>
              <div className="text-lg font-semibold text-navy-900 mb-1">Projects Delivered</div>
              <div className="text-gray-600">Across Multiple Industries</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">50+</div>
              <div className="text-lg font-semibold text-navy-900 mb-1">Regulatory Inspections</div>
              <div className="text-gray-600">Successfully Supported</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">25+</div>
              <div className="text-lg font-semibold text-navy-900 mb-1">FDA, MHRA, EMA</div>
              <div className="text-gray-600">Inspection Preparations</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};